import React, { useState, useEffect } from 'react';
import { supabase } from 'lib/supabase';
import Icon from '../AppIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const ListaDipendenti = ({ idAzienda }) => {
  const { t } = useTranslation();
  const [dipendenti, setDipendenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true); 
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDipendenti = async () => {
      if (!idAzienda) return;
      setLoading(true);
      const { data, error } = await supabase
        .from('profili')
        .select('id, nome, cognome, ruolo, email, attivo')
        .eq('id_azienda', idAzienda)
        .order('cognome', { ascending: true });

      if (error) {
        console.error("Errore fetch dipendenti:", error);
      } else {
        setDipendenti(data);
      }
      setLoading(false);
    };
    fetchDipendenti();
  }, [idAzienda]);

  const filteredDipendenti = dipendenti.filter(d => {
    const matchesStatus = showActiveOnly ? d.attivo === true : d.attivo === false;
    const matchesSearch = `${d.nome} ${d.cognome}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 bg-card rounded-xl border border-border">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin text-primary">
            <Icon name="Loader2" size={32} />
          </div>
          <span className="text-sm text-muted-foreground">{t('dipendenti_list.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      {/* Header Tabele */}
      <div className="p-4 md:p-6 border-b border-border bg-muted/30 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div>
            <h2 className="text-lg font-bold text-foreground">{t('dipendenti_list.title')}</h2>
            <p className="text-xs md:text-sm text-muted-foreground">
              {t('dipendenti_list.subtitle')}
            </p>
          </div>

          {/* Filter Toggle */}
          <div className="flex bg-background border border-border p-1 rounded-xl w-fit shadow-sm">
            <button
              onClick={() => setShowActiveOnly(true)}
              className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-200 ${
                showActiveOnly 
                ? 'bg-primary text-white shadow-md shadow-primary/20' 
                : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('dipendenti_list.filter_active')}
            </button>
            <button
              onClick={() => setShowActiveOnly(false)}
              className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-200 ${
                !showActiveOnly 
                ? 'bg-destructive text-white shadow-md shadow-destructive/20' 
                : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('dipendenti_list.filter_inactive')}
            </button>
          </div>
        </div>
        
        <div className="relative">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-muted-foreground">
            <Icon name="Search" size={16} />
          </div>
          <input
            type="text"
            placeholder={t('dipendenti_list.search_placeholder')}
            className="pl-10 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 w-full md:w-64 transition-all font-medium"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto max-h-[500px] overflow-y-auto scrollbar-thin scrollbar-thumb-border">
        <table className="w-full text-left border-collapse relative">
          <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm">
            <tr>
              <th className="p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border">{t('dipendenti_list.table.name')}</th>
              <th className="p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border">{t('dipendenti_list.table.email')}</th>
              <th className="p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border">{t('dipendenti_list.table.role')}</th>
              <th className="p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground border-b border-border text-right">{t('dipendenti_list.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredDipendenti.length > 0 ? (
              filteredDipendenti.map((radnik) => (
                <tr key={radnik.id} className="hover:bg-muted/30 transition-colors duration-200 group animate-in fade-in slide-in-from-left-1">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border shrink-0 transition-colors ${
                        radnik.attivo === false 
                        ? 'bg-destructive/10 text-destructive border-destructive/20' 
                        : 'bg-primary/10 text-primary border-primary/20'
                      }`}>
                        {radnik.nome[0]}{radnik.cognome[0]}
                      </div>
                      
                      <div className={`text-sm font-bold transition-all truncate max-w-[150px] ${
                        radnik.attivo === false 
                        ? 'text-destructive line-through opacity-60' 
                        : 'text-foreground group-hover:text-primary'
                      }`}>
                        {radnik.nome} {radnik.cognome}
                      </div>
                    </div>
                  </td>
                  <td className={`p-4 text-sm truncate max-w-[200px] font-medium ${radnik.attivo === false ? 'text-destructive/40' : 'text-muted-foreground'}`}>
                    {radnik.email}
                  </td>
                  <td className="p-4 text-sm">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border transition-all ${
                      radnik.attivo === false
                      ? 'bg-destructive/5 text-destructive border-destructive/10 opacity-60'
                      : (radnik.ruolo === 'Admin' || radnik.ruolo === 'Owner'
                        ? 'bg-accent/10 text-accent border border-accent/20' 
                        : 'bg-success/10 text-success border border-success/20')
                    }`}>
                      {radnik.ruolo}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <button 
                      className={`p-2 rounded-lg transition-all shadow-sm border border-transparent ${
                        radnik.attivo === false 
                        ? 'hover:bg-destructive hover:text-white text-destructive border-destructive/10' 
                        : 'hover:bg-primary hover:text-white text-primary hover:border-primary/20'
                      }`}
                      onClick={() => navigate(`/dipendente/${radnik.id}`)}
                    >
                      <Icon name="ArrowRight" size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="p-12 text-center text-muted-foreground">
                  <div className="flex flex-col items-center opacity-20">
                    <Icon name="Users" size={48} className="mb-2" />
                    <p className="text-sm font-bold uppercase tracking-widest">{t('dipendenti_list.no_results')}</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListaDipendenti;